package org.multiplatform.kotfolio.pages.kotlin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/kotlin/mutex")
@Composable
fun MutexPage() {
    BlogLayout(
        PageProperties(
            "Mutex",
            ""
        ),
        BlogProperties("kotlin", "Race condition, Volatile, atomic, synchronized, Mutex (Latest way)")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                SpanText(
                    text = "ConcurrentHashMap",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ConcurrentHashMap is a thread-safe variant of the HashMap designed for concurrent access by multiple threads. Unlike HashMap, which can be corrupted by concurrent modifications, ConcurrentHashMap allows safe and efficient read/write operations in a multi-threaded environment without locking the entire map.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Multiple threads frequently read and write to the map.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "High performance is required without sacrificing thread safety.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Lock contention must be minimized.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Caching shared data in web applications.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Maintaining session information in multi-threaded servers.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Real-time analytics where data is constantly updated.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                    }
                }

                SpanText(
                    text = "Race condition - (Volatile, atomic, synchronized)",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If multiple threads call increment() at the same time, the result is not guaranteed to be correct. Multiple threads can interfere → race condition",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        var count = 0
                        
                        fun increment() {
                            count++
                        }
                    """.trimIndent()
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin objects are lazy-initialized by default, but still thread-safe.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A race condition happens when two or more threads try to use the same data at the same time. Because the system can switch between threads at any moment, you can’t predict which thread will access the data first. As a result, the outcome depends on the timing of the threads — basically, the threads are “racing” to change the data, and whoever gets there first wins.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                        class Logger private constructor() {

                            companion object {

                                @Volatile
                                private var instance: Logger? = null

                                fun getInstance(): Logger {
                                    // First check (no locking)
                                    if (instance == null) {
                                        synchronized(this) {
                                            // Second check (with locking)
                                            if (instance == null) {
                                                println("Creating instance by ${Thread.currentThread().name}")
                                                instance = Logger()
                                            }
                                        }
                                    }
                            return instance ?: throw IllegalStateException("Logger instance is null")
                                }
                            }
                        }
                    """.trimIndent()
                )

                SpanText(
                    text = "synchronized",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Only one thread can enter the synchronized block at a time. Other threads are blocked until the lock is released.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "Atomic",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin (on JVM) uses Java’s atomic classes. Uses CAS (Compare-And-Swap) at CPU level. No locks. If conflict occurs → retry.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "Mutex",
                    modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Mutex stands for Mutual Exclusion, a mechanism that ensures only one thread or coroutine accesses a shared resource at a time. This prevents race conditions, where multiple entities attempt to modify a resource concurrently, leading to unpredictable results.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Mutex acts similarly, controlling access to shared data within your Kotlin application. It functions as a mutual exclusion lock, guaranteeing that only one coroutine can manipulate the shared resource at any given moment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Mutex (Mutual Exclusion) in kotlin is a synchronization primitive that is used to ensure that only one thread can access a shared resource at a time. It helps to prevent race conditions when multiple threads try to access or modify shared data simultaneously. Kotlin provides the Mutex class as part of its coroutines library, which makes it ideal for synchronizing coroutines as well.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Mutex allows only one thread (or coroutine) to execute a critical section at a time. It ensures that no two threads or coroutines can modify the same resource simultaneously, preventing conflicts and inconsistent states. Once a thread/coroutine locks the Mutex, all other threads attempting to lock it will be blocked until the Mutex is unlocked.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Mutex in Kotlin Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )


            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Mutex ensures that only one coroutine accesses a critical section at a time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin's coroutines library (kotlinx.coroutines) provides a non-blocking Mutex for synchronizing coroutine execution. Unlike traditional threading models, coroutines are lightweight and can be suspended without blocking the underlying thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Mutex class: Mutex is available in the kotlinx.coroutines.sync package.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thread-Safe: Only one coroutine accesses the critical section at a time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lightweight: Avoids the overhead associated with traditional thread-based locks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Non-blocking Execution: Coroutines waiting for a Mutex do not block the thread but instead suspend, allowing the thread to perform other work. (Coroutines waiting for a lock are suspended instead of blocking threads, ensuring optimal resource usage.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Explain Mutex in coroutines and why it’s preferred over synchronized for suspend functions.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Mutex.withLock{} provides suspend-friendly mutual exclusion — unlike synchronized, it won’t block the underlying thread while waiting, and suspend functions can’t safely be called inside a synchronized block anyway (risk of deadlocks/hard-to-debug thread pinning behavior).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is Mutex and How It Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Mutex, short for mutual exclusion, is a lock that ensures only one coroutine can execute a block of code at a time. While one coroutine holds the lock, any other coroutine that tries to acquire it suspends and waits.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A real world parallel: A single key for a shared office. Only one person can have the key at a time. If someone else wants in they wait at the door until the key is returned. Nobody forces their way in. Nobody gets lost. Access is orderly.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In coroutines the Mutex works the same way. The withLock function acquires the lock, runs the block, and releases the lock when done. If another coroutine tries to acquire the same lock while it is held, it suspends until the lock is free.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Critically, Mutex in Kotlin coroutines is not a blocking lock. It is a suspending lock. A waiting coroutine suspends rather than blocking its thread, which means it plays nicely with the coroutine scheduler and does not waste thread resources while waiting.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val mutex = Mutex()

                    mutex.withLock {
                        // Only one coroutine can be here at a time
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class OrderViewModel : ViewModel() {
                        private val _orders = MutableStateFlow<List<Order>>(emptyList())
                        val orders: StateFlow<List<Order>> = _orders.asStateFlow()
                        private val mutex = Mutex()

                        fun addOrder(order: Order) {
                            viewModelScope.launch {
                                mutex.withLock {
                                    val current = _orders.value
                                    val updated = current + order
                                    _orders.value = updated
                                }
                            }
                        }
                    }
                    
                    fun syncOrders() {
                        viewModelScope.launch {
                            val remoteOrders = withContext(Dispatchers.IO) {
                                orderRepository.fetchOrders() // Outside the lock
                            }
                            mutex.withLock {
                                _orders.value = remoteOrders // Only the write is locked
                            }
                        }
                    }
                    
                    class SyncManager {
                        private val isSyncing = AtomicBoolean(false)

                        fun startSync() {
                            if (isSyncing.compareAndSet(false, true)) {
                                viewModelScope.launch {
                                    try {
                                        performSync()
                                    } finally {
                                        isSyncing.set(false)
                                    }
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Key Characteristics",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Two states: locked / unlocked",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Non-reentrant (same coroutine cannot re-lock)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Fair (FIFO)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Suspends instead of blocking threads",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Key Methods of Mutex",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "lock(): Acquires the lock on the mutex. If the mutex is already locked by another coroutine, the calling coroutine suspends until the mutex is unlocked. (If the Mutex is unlocked, the coroutine gains the lock and proceeds to execute the critical section — the code block that requires exclusive access. If the Mutex is already locked by another coroutine, the calling coroutine is suspended (paused) until the lock becomes available, preventing race conditions.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "tryLock(): Attempts to acquire the lock but does not suspend if the mutex is already locked. It returns true if the lock is acquired and false otherwise.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "unlock(): Releases the lock on the mutex, allowing another suspended coroutine to acquire the lock. (Once the coroutine finishes its task within the critical section, it releases the lock using the unlock() function. This allows other waiting coroutines a chance to acquire the lock.)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "withLock { ... }: This function simplifies the use of mutexes by acquiring and releasing the lock around a given critical section in a safe manner (similar to synchronized blocks in Java). It ensures that the mutex is unlocked after the block is executed, even if an exception occurs.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    val counter = MutableStateFlow(0) // Shared mutable state
                    val mutex = Mutex()

                    fun incrementCounter() = launch {
                        mutex.lock() // Acquire the lock
                        try {
                            counter.value++ // Modify the shared state
                            delay(100) // Simulate some work
                        } finally {
                            mutex.unlock() // Release the lock for others
                        }
                    }

                    runBlocking {
                        val job1 = launch { incrementCounter() }
                        val job2 = launch { incrementCounter() }
                        job1.join()
                        job2.join()
                        println(counter.value) // Should print 2, ensuring proper synchronization
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.sync.Mutex
                    import kotlinx.coroutines.sync.withLock

                    val mutex = Mutex()
                    var counter = 0

                    fun main() = runBlocking {
                        val jobs = List(10) {
                            launch {
                                mutex.withLock {
                                    counter++
                                    println("Counter: $counter")
                                }
                            }
                        }
                        jobs.joinAll()
                        println("Final Counter: $counter")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val balanceMutex = Mutex()
                    var balance = 1000

                    suspend fun deposit(amount: Int) = balanceMutex.withLock {
                        balance += amount
                    }

                    suspend fun withdraw(amount: Int) = balanceMutex.withLock {
                        if (balance >= amount) balance -= amount
                    }
                    """.trimIndent()
            )

            //https://github.com/toby-gamez/TKOlympApp.git
            KotlinCode(
                code = $$"""
                    data class CacheEntry<T>(
                        val data: T,
                        val timestamp: Instant,
                        val ttl: Duration
                    ) {
                        fun isValid(): Boolean = kotlin.time.Clock.System.now() < timestamp + ttl
                    }

                    class CacheService {
                        private val cache = mutableMapOf<String, CacheEntry<*>>()
                        private val mutex = Mutex()

                        suspend fun <T> get(key: String): T? = withContext(Dispatchers.Default) {
                            @Suppress("UNCHECKED_CAST")
                            mutex.withLock {
                                val entry = cache[key] as? CacheEntry<T>
                                if (entry == null) {
                                    Logger.d("CacheService", "get: MISS for key=$key")
                                    return@withLock null
                                }
                                if (entry.isValid()) {
                                    Logger.d("CacheService", "get: HIT for key=$key")
                                    entry.data
                                } else {
                                    Logger.d("CacheService", "get: EXPIRED for key=$key, removing")
                                    cache.remove(key)
                                    null
                                }
                            }
                        }

                        suspend fun <T> put(key: String, value: T, ttl: Duration = 5.minutes) = withContext(Dispatchers.Default) {
                            mutex.withLock {
                                Logger.d("CacheService", "put: key=$key ttl=${ttl.inWholeMilliseconds}ms")
                                cache[key] = CacheEntry(value, kotlin.time.Clock.System.now(), ttl)
                            }
                        }

                        suspend fun invalidate(key: String) = withContext(Dispatchers.Default) {
                            mutex.withLock { cache.remove(key) }
                        }

                        suspend fun invalidatePrefix(prefix: String) = withContext(Dispatchers.Default) {
                            mutex.withLock {
                                val toRemove = cache.keys.filter { it.startsWith(prefix) }
                                toRemove.forEach { cache.remove(it) }
                                Logger.d("CacheService", "invalidatePrefix: prefix=$prefix removed=${toRemove.size}")
                            }
                        }

                        suspend fun clear() = withContext(Dispatchers.Default) {
                            mutex.withLock { cache.clear() }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Mutex vs Synchronized",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Traditional Synchronized: In traditional multi-threaded environments, synchronized blocks (in Java, for example) are used to ensure that only one thread accesses a critical section at a time. However, synchronized blocks block the thread and may lead to thread contention.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Kotlin Mutex: Kotlin's Mutex is designed for coroutines and works in a non-blocking manner. It suspends coroutines instead of blocking threads, allowing for more efficient multitasking.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Mutex vs ReentrantLock",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A Mutex in Kotlin is not reentrant, meaning that if a coroutine tries to lock a mutex it has already locked, it will suspend itself indefinitely, leading to a deadlock. This is different from ReentrantLock in Java, which allows the same thread to lock multiple times.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If you need reentrant behavior (i.e., the same coroutine should be able to acquire the lock multiple times), you should use ReentrantLock from java.util.concurrent.locks, though it's not coroutine-friendly.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Use Cases for Mutex in Kotlin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Shared resource protection: Use a mutex to protect access to shared variables or objects that are being updated by multiple coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Critical sections: When you need to ensure that only one coroutine is performing a sensitive task at a time (e.g., database writes, I/O operations).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Concurrency control: When multiple coroutines need to cooperate on a shared task without interfering with each other.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Fairness in Mutex",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Mutex in Kotlin can also be configured to provide fairness. This means that the coroutines waiting for the mutex will acquire it in the order they requested it, avoiding starvation.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                 val fairMutex = Mutex(locked = false, fair = true)
            """.trimIndent()
            )

            SpanText(
                text = "Mutex vs Atomic Variables",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Column(
                horizontalAlignment = Alignment.Start
            ) {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Atomic Variables: For simple read-modify-write operations (e.g. incrementing a counter), Kotlin offers atomic variables (AtomicInteger, AtomicReference) that can be updated atomically without locks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "AtomicReference: Using AtomicReference is a great way to handle mutable variables in a thread-safe manner without the need for explicit locks. Typically offers better performance for high concurrency scenarios due to the lack of lock contention and overhead. Best for situations where you need atomic updates to a single reference.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    //https://github.com/amary21/AmaryPay.git
                    KotlinCode(
                        code = """
                             //iosMain.kt
                             
                             import kotlin.concurrent.AtomicReference
                             import platform.Foundation.NSDate
                             import platform.Foundation.timeIntervalSince1970

                             /** iOS implementation of FCMManager - Firebase initialization done in Swift */
                             actual class FCMManager {
                                 private var eventHandler: ((NotificationData) -> Unit)? = null

                                 companion object {
                                     private val instanceRef = AtomicReference<FCMManager?>(null)

                                     fun getInstance(): FCMManager {
                                         instanceRef.value?.let { return it }
                                         val newInstance = FCMManager()
                                         return if (instanceRef.compareAndSet(null, newInstance)) {
                                             newInstance
                                         } else {
                                             instanceRef.value ?: newInstance
                                         }
                                     }

                                     private fun currentTimeMillis(): Long =
                                         (NSDate().timeIntervalSince1970 * 1000).toLong()
                                 }
                             }
                             
                             fun initKoin() {
                                 startKoin {
                                     modules(appModule)
                                 }

                                 // Setup FCM after Koin is initialized
                                 val stateManager = getKoin().get<AppStateManager>()
                                 val eventHandler = EventHandler(stateManager)
                                 val fcmManager = FCMManager.getInstance()

                                 fcmManager.initialize()
                                 fcmManager.setEventHandler { notification ->
                                     eventHandler.handleNotification(notification)
                                 }
                             }
                        """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Mutex: Use a Mutex when more complex synchronization is needed, such as protecting multiple variables or performing a sequence of operations atomically",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "SHA-256 signing certificate",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/toby-gamez/TKOlympApp.git
            KotlinCode(
                code = """
                    /**
                     * Checks whether the running app binary was signed by the official release key.
                     *
                     * On Android the check compares the SHA-256 fingerprint of the installed APK's
                     * signing certificate against the expected value baked into [EXPECTED_CERT_SHA256].
                     *
                     * On iOS a no-op implementation is provided (code-signing enforcement is handled
                     * by the OS and App Store).
                     */
                    interface IIntegrityService {
                        /**
                         * Returns `true` when the app passes the integrity check, `false` otherwise.
                         * Must be called from a coroutine (may perform I/O on Android).
                         */
                        suspend fun isValid(): Boolean
                    }

                    import android.content.Context
                    import android.content.pm.PackageManager
                    import android.os.Build
                    import java.security.MessageDigest

                    /**
                     * Expected SHA-256 fingerprint of the release signing certificate.
                     *
                     * Replace this value with the output of:
                     *   keytool -list -v -keystore <your-keystore.jks> -storepass <password> | grep "SHA256:"
                     *
                     * Format: colon-separated uppercase hex pairs, e.g.
                     *   "AB:CD:EF:01:23:45:67:89:AB:CD:EF:01:23:45:67:89:AB:CD:EF:01:23:45:67:89:AB:CD:EF:01:23:45:67"
                     */
                    private const val EXPECTED_CERT_SHA256 = "99:12:DC:5E:75:5A:3E:88:4A:42:A0:01:64:E8:43:A8:18:D1:F8:DD:1C:B9:1B:AA:49:12:76:B7:5F:8B:5B:C9"

                    class IntegrityServiceAndroid(private val context: Context) : IIntegrityService {

                        override suspend fun isValid(): Boolean {
                            return try {
                                val fingerprint = getSigningCertSha256()
                                val valid = fingerprint.equals(EXPECTED_CERT_SHA256, ignoreCase = true)
                                if (!valid) {
                                    Logger.d("IntegrityCheck", "Certificate fingerprint mismatch: ${'$'}fingerprint")
                                }
                                valid
                            } catch (e: Exception) {
                                Logger.d("IntegrityCheck", "Integrity check failed with exception: ${'$'}{e.message}")
                                false
                            }
                        }

                        @Suppress("DEPRECATION")
                        private fun getSigningCertSha256(): String {
                            val signatures = if (Build.VERSION.SDK_INT >= Build.VERSION_CODES.P) {
                                val info = context.packageManager.getPackageInfo(
                                    context.packageName,
                                    PackageManager.GET_SIGNING_CERTIFICATES
                                )
                                info.signingInfo?.apkContentsSigners ?: emptyArray()
                            } else {
                                val info = context.packageManager.getPackageInfo(
                                    context.packageName,
                                    PackageManager.GET_SIGNATURES
                                )
                                info.signatures ?: emptyArray()
                            }

                            if (signatures.isEmpty()) return ""

                            val cert = signatures[0].toByteArray()
                            val digest = MessageDigest.getInstance("SHA-256").digest(cert)
                            return digest.joinToString(":") { "%02X".format(it) }
                        }
                    }
                    
                    //UI
                    // Skip integrity check in debug builds
                    if (!BuildConfig.DEBUG) {
                        val integrityOk = IntegrityServiceAndroid(ctx).isValid()
                        if (!integrityOk) {
                            integrityFailed = true
                            return@LaunchedEffect
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}
//https://github.com/islamzadavusal/Snake-Game
//https://androidmeda.medium.com/concurrency-safety-in-android-race-conditions-mutex-and-ui-state-73132648eeff